import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { ContentType } from "@/types/ab-tests";
import { getBulletIndex } from "@/utils/ab-test-utils";

interface VariantContentEditorProps {
  selectedFields: ContentType[];
  variantATitle: string;
  variantABullets: Record<string, string>;
  variantBTitle: string;
  variantBBullets: Record<string, string>;
  onVariantBTitleChange: (value: string) => void;
  onVariantBBulletChange: (field: string, value: string) => void;
  isGenerating: boolean;
  onGenerate: () => void;
}

interface FieldEditorProps {
  field: ContentType;
  isVariantB: boolean;
  titleValue: string;
  bulletsValue: Record<string, string>;
  onTitleChange?: (value: string) => void;
  onBulletChange?: (field: string, value: string) => void;
}

function FieldEditor({ field, isVariantB, titleValue, bulletsValue, onTitleChange, onBulletChange }: FieldEditorProps) {
  const isTitle = field === "title";
  const idx = getBulletIndex(field);
  const label = isTitle ? "Title" : `Bullet ${(idx ?? 0) + 1}`;
  const fieldValue = isTitle ? titleValue : (bulletsValue[field] || "");
  const maxLen = isTitle ? 200 : 500;

  return (
    <div className="space-y-1">
      <p className="text-sm text-foreground">{label}</p>
      <div className="rounded-md border border-border bg-muted/50 px-3 py-2 h-[140px]">
        <textarea
          placeholder={isVariantB ? `Enter variant ${label.toLowerCase()}...` : (isTitle ? "Variant A title" : "No current bullet")}
          value={fieldValue}
          readOnly={!isVariantB}
          onChange={isVariantB ? (e) => {
            if (isTitle) onTitleChange?.(e.target.value);
            else onBulletChange?.(field, e.target.value);
          } : undefined}
          className="w-full h-full resize-none bg-transparent text-xs leading-relaxed text-foreground placeholder:text-muted-foreground focus:outline-none cursor-default"
          maxLength={maxLen}
        />
        <p className="text-[10px] text-muted-foreground text-right mt-1">{fieldValue.length} / {maxLen}</p>
      </div>
    </div>
  );
}

export function VariantContentEditor({
  selectedFields,
  variantATitle,
  variantABullets,
  variantBTitle,
  variantBBullets,
  onVariantBTitleChange,
  onVariantBBulletChange,
  isGenerating,
  onGenerate,
}: VariantContentEditorProps) {
  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-3">
          <p className="text-sm font-semibold text-foreground">Variant A</p>
          <div className="space-y-3">
            {selectedFields.map((field) => (
              <FieldEditor
                key={field}
                field={field}
                isVariantB={false}
                titleValue={variantATitle}
                bulletsValue={variantABullets}
              />
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <p className="text-sm font-semibold text-foreground">Variant B</p>
          <div className="space-y-3">
            {selectedFields.map((field) => (
              <FieldEditor
                key={field}
                field={field}
                isVariantB={true}
                titleValue={variantBTitle}
                bulletsValue={variantBBullets}
                onTitleChange={onVariantBTitleChange}
                onBulletChange={onVariantBBulletChange}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="flex justify-end" style={{ marginTop: "2rem" }}>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onGenerate}
          disabled={isGenerating}
          style={{ width: "calc(50% - 0.5rem)" }}
          className="py-2.5 text-sm h-auto"
        >
          <Sparkles className="h-3 w-3" />
          {isGenerating ? "Generating..." : "Generate with AI"}
        </Button>
      </div>
    </>
  );
}
